import { useQuery } from '@tanstack/react-query';
import { db, describeDbError } from '../lib/db';
import {
  ESTIMATE_DETAIL_COLUMNS,
  LINE_ITEM_COLUMNS,
  type EstimateDetailRow,
  type EstimateLineItemRow,
} from '../lib/estimateTypes';

export interface EstimateWithItems {
  estimate: EstimateDetailRow;
  lineItems: EstimateLineItemRow[];
}

export const estimateQueryKey = (id: string | undefined) => ['estimate', id] as const;

export function useEstimate(
  id: string | undefined,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: estimateQueryKey(id),
    enabled: !!id,
    refetchInterval: options?.refetchInterval ?? false,
    queryFn: async (): Promise<EstimateWithItems> => {
      if (!id) throw new Error('Estimate ID required');
      const [estRes, itemsRes] = await Promise.all([
        db.from('estimates').select(ESTIMATE_DETAIL_COLUMNS).eq('id', id).maybeSingle(),
        db.from('estimate_line_items').select(LINE_ITEM_COLUMNS).eq('estimate_id', id).order('sort_order'),
      ]);
      if (estRes.error) throw new Error(describeDbError(estRes.error, 'Could not load the estimate.'));
      if (!estRes.data) throw new Error('Estimate not found.');
      if (itemsRes.error) throw new Error(describeDbError(itemsRes.error, 'Could not load line items.'));
      return {
        estimate: estRes.data as EstimateDetailRow,
        lineItems: (itemsRes.data ?? []) as EstimateLineItemRow[],
      };
    },
    staleTime: 60 * 1000,
  });
}
